import { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { GetApp as GetAppIcon, Close as CloseIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import { usePwaInstall } from '../hooks/use-pwa-install';
import PWAInstallGuideDialog from './PWAInstallGuideDialog';

const DISMISS_KEY = 'galldris-pwa-install-dismissed';

export default function PWAInstallPrompt() {
  const { isStandalone, canNativeInstall, platform, promptInstall, showInstallOption } = usePwaInstall();
  const [dismissed, setDismissed] = useState(true);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  const handleInstall = async () => {
    if (platform.isInAppBrowser || !canNativeInstall) {
      setGuideOpen(true);
      return;
    }
    const { outcome } = await promptInstall();
    if (outcome !== 'accepted') {
      setGuideOpen(true);
    }
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {}
    setDismissed(true);
  };

  const show = showInstallOption && !dismissed && (canNativeInstall || platform.isIOS || platform.isAndroid);

  if (!show) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <GetAppIcon color="primary" sx={{ flexShrink: 0 }} />
          <Typography variant="body2" color="text.primary">
            Install Galldris for quick access.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleInstall}
            sx={{ textTransform: 'none' }}
          >
            {canNativeInstall ? 'Install' : 'How to install'}
          </Button>
          {!canNativeInstall && (
            <IconButton size="small" aria-label="Install help" onClick={() => setGuideOpen(true)}>
              <HelpIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" aria-label="Dismiss" onClick={handleDismiss}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <PWAInstallGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        platform={platform}
      />
    </>
  );
}
